import React, { useEffect, useState, useMemo } from 'react'
import AttachImageToTree from '@fs/zion-attach-image-to-tree'
import { InfoSheet } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import zionDebug from '@fs/zion-debug'
import { getImage, getNestedOrFloatingElementById, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import { useIsOptedIn } from '@fs/zion-soft-launch'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2StateContext } from '../../providers/r2Provider/R2Context'
import useRecords from '../../views/indexing/hooks/useRecords'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import useInfoSheetOrientationCachedState from '../../../shared/hooks/useInfoSheetOrientationCachedState'
import RipInfoSheetBody from './SideSheetBody'
import ManageNamesPanel from '../generic/panels/manageNamesPanel/ManageNamesPanel'
import useAttachImageToTreeData from '../../hooks/useAttachImageToTreeData'

const debug = zionDebug('siv:RipInfoSheetWrapper')

const SideSheet = ({ loading, imageTitle }) => {
  const { infoSheetOverlay, openOverlay, allPanelsClosed, attachToTreeOverlay, topMostPanel } = useOverlayContext()
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)
  const h = useSlsHighlightsContext()
  const {
    rip_sharedImageViewer_fullTextAttachFlowOptIn: fullTextAttachFlowFlagOptIn,
    rip_sharedImageViewer_fullTextAttachFlow_v1: fullTextAttachFlowV1Flag,
    rip_sharedImageViewer_fullTextAttachFlow_v2: fullTextAttachFlowV2Flag,
  } = useFeatureFlags([
    'rip_sharedImageViewer_fullTextAttachFlowOptIn',
    'rip_sharedImageViewer_fullTextAttachFlow_v1',
    'rip_sharedImageViewer_fullTextAttachFlow_v2',
  ])
  const { isOptedIn, loading: loadingAttachFlowPrefs } = useIsOptedIn({
    featureFlag: 'rip_sharedImageViewer_fullTextAttachFlow_v1',
  })
  let fullTextAttachFlowV1IsOn = fullTextAttachFlowV1Flag.isOn
  if (fullTextAttachFlowFlagOptIn.isOn) {
    fullTextAttachFlowV1IsOn =
      r2State.attachFlowOptInLocalChange === null
        ? !loadingAttachFlowPrefs && isOptedIn
        : r2State.attachFlowOptInLocalChange
  }

  const thumbnails = sharedBrowserState.thumbnails
  const { imageHasData } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })

  const { isBottomSheet, setInfoSheetOrientation } = useInfoSheetOrientationCachedState()
  const attachImageToTreeData = useAttachImageToTreeData(imageTitle)

  const { query, removeQuery } = useQueryParams({ replaceState: true })
  const personId = query?.personArk?.split('/').find((str) => str.includes('1:1'))
  const action = query?.action

  // Get the current view title based on query and mode
  const currentViewTitle = useMemo(() => {
    switch (query.view) {
      case 'index':
        return t('headerNavigation.names', 'Names')
      case 'fullText':
        return t('headerNavigation.transcript', 'Transcript')
      default:
        return t('headerNavigation.information', 'Information')
    }
  }, [query.view, t])

  const [once, setOnce] = useState(false)

  // Where should this global check go?  We need to reset highlights if all panels get closed
  useEffect(() => {
    if (allPanelsClosed() && query.view === 'index') {
      const fieldTypes = ['NAME_SURN', 'NAME_SUF', 'NAME_GN']
      const customHighlightsFilter = (hl) => hl.personIds.length > 0
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark

      if (zionSlsState?.currentImageArk) {
        h.setDisplayHighlights({
          mainPartitionKey: zionSlsState.currentImageArk,
          fieldTypes,
          customFilter: customHighlightsFilter,
        })
      }

      if (r2State.splitScreen && !r2State.splitScreenImageGrid && splitScreenImageArk) {
        h.setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          fieldTypes,
          customFilter: customHighlightsFilter,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allPanelsClosed,
    r2Dispatch,
    r2State.editHighlights,
    r2State.highlights,
    r2State.referenceEditHighlights,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    zionSlsState.currentImageArk,
    thumbnails,
    query.view,
    r2State.splitScreenImageGrid,
  ])

  useEffect(() => {
    let mounted = true

    // Gameplan:
    // 1. The R2Image component sets the `imageLoaded` state to true once the image has fully loaded.
    // 2. After the image is loaded, the main InfoSheet overlay is opened.
    // 3. If a `personId` is present in the query parameters, the viewIndex (PersonOverview) overlay is opened on top of the main InfoSheet.
    // 4. This ensures the overlay stack is correctly ordered and the PersonOverview component can apply highlights properly.
    if (!loading && !once && mounted && sharedBrowserState.imageLoaded) {
      if (action) {
        const selectedPerson = getNestedOrFloatingElementById([currentImage], personId)
        if (action === 'view' && selectedPerson) {
          openOverlay('viewIndex')
          r2Dispatch({ type: 'NAV_SELECT_TO_VIEW', value: { personId, recordId: selectedPerson.recordId } })
        } else {
          // Person isn't found on this image, so remove the param
          removeQuery('action')
          removeQuery('personArk')
        }
      } else if (!topMostPanel()) {
        // If coming back from history viewer, this could be set
        r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', value: null })
        removeQuery('personArk')
      }
      setOnce(true)
    }

    return () => {
      mounted = false
    }
  }, [
    action,
    loading,
    once,
    openOverlay,
    personId,
    r2Dispatch,
    zionSlsState.images,
    currentImage,
    removeQuery,
    topMostPanel,
    sharedBrowserState.imageLoaded,
  ])

  debug({ imageHasData, zionSlsState })

  // When v1 flag is on, use smaller panel size in fullText view
  // When v2 flag is on, use larger panel size in all views (including index, fullText, and Information/Explore)
  const showLargeSideSheet = fullTextAttachFlowV2Flag.isOn || (query.view === 'fullText' && !fullTextAttachFlowV1IsOn)

  return (
    <InfoSheet
      dockable={!fullTextAttachFlowV2Flag.isOn && !fullTextAttachFlowV1IsOn} // Disable docking when the fullText attach flow v1 or v2 is enabled (docking is not supported in the v1 or v2 flow/cards)
      size={showLargeSideSheet ? 'lg' : 'md'}
      title={currentViewTitle}
      {...infoSheetOverlay}
      isBottomSheet={isBottomSheet}
      setIsBottomSheet={setInfoSheetOrientation}
    >
      <RipInfoSheetBody
        {...{
          imageHasData,
          isBottomSheet,
          loading,
          fullTextAttachFlowV1IsOn,
        }}
      />
      {!fullTextAttachFlowV2Flag.isOn && (
        <AttachImageToTree {...attachImageToTreeData} attachToTreeFirstPanel={attachToTreeOverlay} />
      )}
      <ManageNamesPanel />
    </InfoSheet>
  )
}

export default SideSheet
